import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiErr } from "../lib/api";
import { Coins, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const fieldCls = "w-full mb-4 px-3 py-2.5 bg-[color:var(--surface)] border border-[color:var(--border-subtle)] text-white text-sm focus:border-gold focus:outline-none transition-colors duration-200";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("signup") ? "signup" : "login");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const signup = mode === "signup";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const u = signup ? await register(email, password, name, phone) : await login(email, password);
      toast.success(signup ? `Welcome to Fateh FX, ${u.name}!` : `Welcome back, ${u.name}`);
      nav(u.role === "client" ? "/portal" : u.role === "team_member" ? "/team-portal" : "/dashboard");
    } catch (e2) {
      setErr(apiErr(e2, signup ? "Sign up failed" : "Login failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex grain" style={{ background: "var(--app-bg)" }}>
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden border-r border-[color:var(--border-subtle)]">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1714515982656-3f8b7bfba88a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(5,5,10,0.85), rgba(5,5,10,0.55))" }} />
        <div className="relative flex items-center gap-2">
          <div className="w-9 h-9 flex items-center justify-center bg-gold text-black font-head font-bold">F</div>
          <span className="font-head font-bold text-xl text-white tracking-tight">Fateh FX</span>
        </div>
        <div className="relative">
          <h1 className="font-head text-4xl xl:text-5xl font-bold text-white tracking-tight leading-tight">
            AI Gold & Bitcoin<br />Research Command Center
          </h1>
          <p className="text-slate-400 mt-4 max-w-md leading-relaxed">
            Institutional-grade XAUUSD & BTCUSD intelligence, automated signal engine, CRM, broker referrals and Telegram automation — in one terminal.
          </p>
          <div className="flex gap-6 mt-8 font-mono">
            <div><div className="text-2xl text-gold font-semibold">XAUUSD</div><div className="text-xs text-slate-500 uppercase tracking-widest">Gold Intelligence</div></div>
            <div><div className="text-2xl text-btc font-semibold">BTCUSD</div><div className="text-xs text-slate-500 uppercase tracking-widest">Bitcoin Intelligence</div></div>
          </div>
        </div>
        <div className="relative text-xs text-slate-600 font-mono">Research only · No guaranteed profits · Trading involves risk</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 flex items-center justify-center bg-gold text-black font-head font-bold">F</div>
            <span className="font-head font-bold text-xl text-white">Fateh FX</span>
          </div>
          <div className="flex mb-6 border border-[color:var(--border-subtle)]" data-testid="auth-mode-switch">
            {[["login", "Sign in"], ["signup", "Sign up free"]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`auth-mode-${k}`} onClick={() => { setMode(k); setErr(""); }}
                className={`flex-1 py-2 text-sm transition-colors duration-200 ${mode === k ? "bg-gold text-black font-semibold" : "text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <h2 className="font-head text-2xl font-semibold text-white tracking-tight">{signup ? "Free account banayein" : "Sign in"}</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">{signup ? "Sign up karke free Gold & Bitcoin signals dekhein" : "Access your account"}</p>

          {err && <div className="mb-4 px-3 py-2 text-sm text-bear border border-[color:var(--bear)] bg-[rgba(239,68,68,0.08)]" data-testid="login-error">{err}</div>}

          {signup && (
            <>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Name</label>
              <input data-testid="signup-name" value={name} onChange={(e) => setName(e.target.value)} required className={fieldCls} />
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">WhatsApp number</label>
              <input data-testid="signup-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="+971 50 123 4567" className={fieldCls} />
            </>
          )}

          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Email</label>
          <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={fieldCls} />

          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Password</label>
          <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={signup ? 6 : undefined}
            className={fieldCls.replace("mb-4", "mb-6")} />

          <button data-testid="login-submit" type="submit" disabled={loading}
            className="btn-gold w-full py-2.5 flex items-center justify-center gap-2 text-sm disabled:opacity-60">
            {loading ? (signup ? "Account ban raha hai…" : "Signing in…") : <>{signup ? "Create free account" : "Sign in"} <ArrowRight size={16} /></>}
          </button>

          <Link to="/app" className="mt-6 inline-flex items-center gap-1 text-sm text-gold hover:underline" data-testid="login-get-app">Android app download karein <ArrowRight size={14} /></Link>
        </form>
      </div>
    </div>
  );
}
